import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, parseISO } from "date-fns";

// =====================
// Core Utilities
// =====================

/**
 * Merge Tailwind classes with clsx and tailwind-merge
 */
export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

// =====================
// Formatters
// =====================

/**
 * Format currency with locale awareness
 */
export function formatCurrency(
	amount: number,
	currency: string = "USD",
	locale: string = "en-US"
): string {
	return new Intl.NumberFormat(locale, {
		style: "currency",
		currency,
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(amount);
}

/**
 * Format date using date-fns
 */
export function formatDate(
	date: string | Date,
	formatString: string = "MMM d, yyyy"
): string {
	const parsedDate = typeof date === "string" ? parseISO(date) : date;
	return format(parsedDate, formatString);
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: string | Date): string {
	const parsedDate = typeof date === "string" ? parseISO(date) : date;
	const now = new Date();
	const diffInSeconds = Math.floor((now.getTime() - parsedDate.getTime()) / 1000);

	if (diffInSeconds < 60) return "just now";
	if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
	if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
	if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

	return formatDate(parsedDate);
}

/**
 * Format phone number
 */
export function formatPhone(phone: string): string {
	const cleaned = phone.replace(/\D/g, "");
	const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
	if (match) {
		return `(${match[1]}) ${match[2]}-${match[3]}`;
	}
	return phone;
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
	if (bytes === 0) return "0 B";
	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB", "TB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

// =====================
// Event Type & Status
// =====================

export type EventType =
	| "gig"
	| "rehearsal"
	| "recording"
	| "workshop"
	| "masterclass"
	| "networking"
	| "album_launch"
	| "custom";

export type EventStatus =
	| "draft"
	| "scheduled"
	| "confirmed"
	| "in-progress"
	| "completed"
	| "cancelled";

const EVENT_TYPE_COLORS: Record<EventType, { bg: string; text: string; border: string }> = {
	gig: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200" },
	rehearsal: { bg: "bg-green-100", text: "text-green-700", border: "border-green-200" },
	recording: { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-200" },
	workshop: { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-200" },
	masterclass: { bg: "bg-yellow-100", text: "text-yellow-700", border: "border-yellow-200" },
	networking: { bg: "bg-pink-100", text: "text-pink-700", border: "border-pink-200" },
	album_launch: { bg: "bg-red-100", text: "text-red-700", border: "border-red-200" },
	custom: { bg: "bg-gray-100 dark:bg-gray-700", text: "text-gray-700 dark:text-gray-300", border: "border-gray-200 dark:border-gray-700" },
};

const EVENT_TYPE_COLORS_DARK: Record<EventType, { bg: string; text: string; border: string }> = {
	gig: { bg: "dark:bg-blue-900/30", text: "dark:text-blue-400", border: "dark:border-blue-800" },
	rehearsal: { bg: "dark:bg-green-900/30", text: "dark:text-green-400", border: "dark:border-green-800" },
	recording: { bg: "dark:bg-purple-900/30", text: "dark:text-purple-400", border: "dark:border-purple-800" },
	workshop: { bg: "dark:bg-orange-900/30", text: "dark:text-orange-400", border: "dark:border-orange-800" },
	masterclass: { bg: "dark:bg-yellow-900/30", text: "dark:text-yellow-400", border: "dark:border-yellow-800" },
	networking: { bg: "dark:bg-pink-900/30", text: "dark:text-pink-400", border: "dark:border-pink-800" },
	album_launch: { bg: "dark:bg-red-900/30", text: "dark:text-red-400", border: "dark:border-red-800" },
	custom: { bg: "dark:bg-gray-900/30", text: "dark:text-gray-400", border: "dark:border-gray-700" },
};

const STATUS_COLORS: Record<EventStatus, { bg: string; text: string; dot: string }> = {
	draft: { bg: "bg-gray-100 dark:bg-gray-700", text: "text-gray-700 dark:text-gray-300", dot: "bg-gray-400" },
	scheduled: { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500" },
	confirmed: { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500" },
	"in-progress": { bg: "bg-orange-100", text: "text-orange-700", dot: "bg-orange-500" },
	completed: { bg: "bg-gray-100 dark:bg-gray-700", text: "text-gray-500 dark:text-gray-400", dot: "bg-gray-400" },
	cancelled: { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500" },
};

const STATUS_COLORS_DARK: Record<EventStatus, { bg: string; text: string; dot: string }> = {
	draft: { bg: "dark:bg-gray-800", text: "dark:text-gray-400", dot: "dark:bg-gray-500" },
	scheduled: { bg: "dark:bg-blue-900/30", text: "dark:text-blue-400", dot: "dark:bg-blue-400" },
	confirmed: { bg: "dark:bg-green-900/30", text: "dark:text-green-400", dot: "dark:bg-green-400" },
	"in-progress": { bg: "dark:bg-orange-900/30", text: "dark:text-orange-400", dot: "dark:bg-orange-400" },
	completed: { bg: "dark:bg-gray-800", text: "dark:text-gray-500", dot: "dark:bg-gray-500" },
	cancelled: { bg: "dark:bg-red-900/30", text: "dark:text-red-400", dot: "dark:bg-red-400" },
};

/**
 * Get color classes for event type
 */
export function getEventTypeColor(type: EventType, darkMode: boolean = false) {
	if (darkMode) {
		return EVENT_TYPE_COLORS_DARK[type] || EVENT_TYPE_COLORS.custom;
	}
	return EVENT_TYPE_COLORS[type] || EVENT_TYPE_COLORS.custom;
}

/**
 * Get color classes for event status
 */
export function getStatusColor(status: EventStatus, darkMode: boolean = false) {
	if (darkMode) {
		return STATUS_COLORS_DARK[status] || STATUS_COLORS.draft;
	}
	return STATUS_COLORS[status] || STATUS_COLORS.draft;
}

/**
 * Format event type for display
 */
export function formatEventType(type: EventType): string {
	const labels: Record<EventType, string> = {
		gig: "Live Gig",
		rehearsal: "Rehearsal",
		recording: "Recording",
		workshop: "Workshop",
		masterclass: "Masterclass",
		networking: "Networking",
		album_launch: "Album Launch",
		custom: "Custom Event",
	};
	return labels[type] || type;
}

/**
 * Format event status for display
 */
export function formatEventStatus(status: EventStatus): string {
	const labels: Record<EventStatus, string> = {
		draft: "Draft",
		scheduled: "Scheduled",
		confirmed: "Confirmed",
		"in-progress": "In Progress",
		completed: "Completed",
		cancelled: "Cancelled",
	};
	return labels[status] || status;
}

// =====================
// Validation Helpers
// =====================

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
}

/**
 * Validate password strength
 */
export function getPasswordStrength(password: string): {
	score: number;
	label: string;
	color: string;
} {
	let score = 0;

	if (password.length >= 8) score++;
	if (password.length >= 12) score++;
	if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
	if (/\d/.test(password)) score++;
	if (/[^a-zA-Z0-9]/.test(password)) score++;

	if (score <= 1) return { score, label: "Weak", color: "bg-red-500" };
	if (score <= 2) return { score, label: "Fair", color: "bg-orange-500" };
	if (score <= 3) return { score, label: "Good", color: "bg-yellow-500" };
	if (score <= 4) return { score, label: "Strong", color: "bg-green-500" };
	return { score: 5, label: "Very Strong", color: "bg-emerald-500" };
}

// =====================
// Array & Object Helpers
// =====================

/**
 * Group array items by key
 */
export function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
	return array.reduce(
		(result, item) => ({
			...result,
			[item[key] as string]: [...(result[item[key] as string] || []), item],
		}),
		{} as Record<string, T[]>
	);
}

/**
 * Sort array by key
 */
export function sortBy<T>(array: T[], key: keyof T, order: "asc" | "desc" = "asc"): T[] {
	return [...array].sort((a, b) => {
		const aVal = a[key];
		const bVal = b[key];

		if (aVal < bVal) return order === "asc" ? -1 : 1;
		if (aVal > bVal) return order === "asc" ? 1 : -1;
		return 0;
	});
}

/**
 * Pick specific keys from object
 */
export function pick<T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
	const result = {} as Pick<T, K>;
	keys.forEach((key) => {
		if (key in obj) {
			result[key] = obj[key];
		}
	});
	return result;
}

/**
 * Omit specific keys from object
 */
export function omit<T extends object, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> {
	const result = { ...obj };
	keys.forEach((key) => {
		delete result[key];
	});
	return result as Omit<T, K>;
}

// =====================
// ID Generation
// =====================

/**
 * Generate unique ID
 */
export function generateId(): string {
	return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Generate short ID (for slugs, etc.)
 */
export function generateShortId(): string {
	return Math.random().toString(36).substr(2, 8);
}

// =====================
// Debounce & Throttle
// =====================

export function debounce<T extends (...args: unknown[]) => unknown>(
	fn: T,
	delay: number
): (...args: Parameters<T>) => void {
	let timeoutId: ReturnType<typeof setTimeout>;
	return (...args: Parameters<T>) => {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => fn(...args), delay);
	};
}

export function throttle<T extends (...args: unknown[]) => unknown>(
	fn: T,
	limit: number
): (...args: Parameters<T>) => void {
	let inThrottle: boolean;
	return (...args: Parameters<T>) => {
		if (!inThrottle) {
			fn(...args);
			inThrottle = true;
			setTimeout(() => (inThrottle = false), limit);
		}
	};
}
